import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  IFetchTagsLttbParams,
  IFetchTagsParams,
  IFetchHistoryTagsLatestParams,
  ISiteToChartDataState,
  IEpochTimeRange,
  StoredChartValues,
} from "../../data_point/charts/ChartModel";
import { validateResponse } from "../../data_point/TagValidationUtils";
import { getTagsHistoryAsync, getTagsHistoryLatestAsync, getTagsHistoryLttbAsync } from "../TagsAPI";
import {
  IStringToTimeValueListDictionary,
  IValueTime,
  PRODUCTION_TO_POTENTIAL_FACTOR,
} from "../../data_point/models/TagsModels";
import { Tags } from "../../data_point/tags/TagCatalog";
import { RootState } from "../Store";
import DateTimeFormatter from "../DateTimeFormatter";
import { LoadingState } from "../LoadingState";
import { chartRangeView, chartSiteView, serializeChartValues } from "./ChartSerialization";

const defaultTagsChartState: ISiteToChartDataState = {};

interface IFetchedTagsPayload {
  siteId: string;
  rangeTime: IEpochTimeRange;
  key: string;
  data: StoredChartValues;
  siteTimeZone?: string;
}

export const multiSitesTagsChartSlice = createSlice({
  name: "multiSitesTagsChart",
  initialState: defaultTagsChartState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTags.fulfilled, (state, action) =>
      updateTagsChartState(state, action.payload, action.meta.requestId)
    );
    builder.addCase(fetchTagsLttb.fulfilled, (state, action) =>
      updateTagsChartState(state, action.payload, action.meta.requestId)
    );
    builder.addCase(fetchTagsLatest.fulfilled, (state, action) =>
      updateTagsChartState(state, action.payload, action.meta.requestId)
    );
    builder.addCase(fetchTags.pending, (state, action) =>
      updateTagsPendingState(
        state,
        action.meta.arg.paramsForGraphDisplayRequest.siteId,
        action.meta.arg.key,
        action.meta.arg.paramsForGraphDisplayRequest.range,
        action.meta.requestId
      )
    );
    builder.addCase(fetchTagsLttb.pending, (state, action) =>
      updateTagsPendingState(
        state,
        action.meta.arg.paramsForGraphDisplayRequest.siteId,
        action.meta.arg.key,
        action.meta.arg.paramsForGraphDisplayRequest.range,
        action.meta.requestId
      )
    );
    builder.addCase(fetchTagsLatest.pending, (state, action) =>
      updateTagsLatestPendingState(state, action)
    );
    builder.addCase(fetchTags.rejected, (state, action) =>
      updateTagsRejectedState(
        state,
        action.meta.arg.paramsForGraphDisplayRequest.siteId,
        action.meta.arg.key,
        action.meta.arg.paramsForGraphDisplayRequest.range,
        action.meta.requestId
      )
    );
    builder.addCase(fetchTagsLttb.rejected, (state, action) =>
      updateTagsRejectedState(
        state,
        action.meta.arg.paramsForGraphDisplayRequest.siteId,
        action.meta.arg.key,
        action.meta.arg.paramsForGraphDisplayRequest.range,
        action.meta.requestId
      )
    );
    builder.addCase(fetchTagsLatest.rejected, (state, action) =>
      updateTagsRejectedState(
        state,
        action.meta.arg.paramsForGraphDisplayRequest.siteId,
        action.meta.arg.key,
        action.meta.arg.paramsForGraphDisplayRequest.range,
        action.meta.requestId
      )
    );
  },
});

const updateTagsPendingState = (
  state: ISiteToChartDataState,
  siteId: string,
  key: string,
  rangeTime: IEpochTimeRange,
  requestId: string
) => {
  if (!state[siteId]) {
    state[siteId] = {};
  }
  const existingData = state[siteId][key];
  state[siteId][key] = {
    tagToValues: existingData?.tagToValues ?? {},
    rangeTime,
    status: LoadingState.Pending,
    activeRequestId: requestId,
    siteTimeZone: existingData?.siteTimeZone,
  };
};

const updateTagsRejectedState = (
  state: ISiteToChartDataState,
  siteId: string,
  key: string,
  rangeTime: IEpochTimeRange,
  requestId: string
) => {
  const existingData = state[siteId]?.[key];
  if (existingData?.activeRequestId !== requestId) {
    return;
  }
  state[siteId][key] = {
    tagToValues: existingData?.tagToValues ?? {},
    rangeTime: existingData?.rangeTime ?? rangeTime,
    status: LoadingState.Error,
    siteTimeZone: existingData?.siteTimeZone,
  };
};

const updateTagsLatestPendingState = (
  state: ISiteToChartDataState,
  action: PayloadAction<
    undefined,
    string,
    { arg: IFetchHistoryTagsLatestParams; requestId: string; requestStatus: "pending" },
    never
  >
) => {
  const { paramsForGraphDisplayRequest, key } = action.meta.arg;
  const siteId = paramsForGraphDisplayRequest.siteId;
  if (!state[siteId]) {
    state[siteId] = {};
  }
  const existingData = state[siteId][key];
  const newRangeTime =
    existingData?.status === LoadingState.Pending || !existingData?.rangeTime
      ? paramsForGraphDisplayRequest.range
      : existingData.rangeTime;

  state[siteId][key] = {
    tagToValues: existingData?.tagToValues ?? {},
    rangeTime: newRangeTime,
    status: LoadingState.Pending,
    activeRequestId: action.meta.requestId,
    siteTimeZone: existingData?.siteTimeZone,
  };
};

const updateTagsChartState = (
  state: ISiteToChartDataState,
  payload: IFetchedTagsPayload,
  requestId: string
) => {
  const { siteId, key, rangeTime, data } = payload;
  if (state[siteId]?.[key]?.activeRequestId !== requestId) {
    return;
  }
  state[siteId][key] = {
    tagToValues: data,
    rangeTime: rangeTime,
    status: LoadingState.Complete,
    siteTimeZone: payload.siteTimeZone,
  };
};

const adjustPotentialByProduction = (
  data: IStringToTimeValueListDictionary,
  potentialValueTimePairs: IValueTime[],
  siteDateTimeFormatter: DateTimeFormatter
) => {
  // We assume that the cumulative production and cumulative potential are always fetched
  // at the same request, i.e. we assume that the data contains the cumulative production values
  // when adjusting the cumulative potential values.
  const cumulativeProductionTags = data[Tags.Portal.DailyProduction.AcPower.logicalName];
  if (!cumulativeProductionTags) {
    return;
  }
  const cumulativeProductionFormattedTimeValueDict = cumulativeProductionTags.reduce(
    (acc, { value, time }) => {
      const formattedProductionDate = siteDateTimeFormatter.formatDate(time);
      acc.set(formattedProductionDate, value);
      return acc;
    },
    new Map<string, number>()
  );

  potentialValueTimePairs.forEach((potentialValueTimePair, index) => {
    const formattedPotentialDate = siteDateTimeFormatter.formatDate(potentialValueTimePair.time);
    const productionValue = cumulativeProductionFormattedTimeValueDict.get(formattedPotentialDate);
    if (!productionValue) {
      return;
    }
    if (potentialValueTimePair.value < productionValue) {
      potentialValueTimePairs[index] = {
        ...potentialValueTimePair,
        value: productionValue * PRODUCTION_TO_POTENTIAL_FACTOR,
      };
    }
  });
};

const requestSiteTimeZone = (state: RootState, siteId: string) =>
  state.multiSitesMetadata?.sites[siteId]?.ianaTimeZoneName ??
  (state.site?.siteId === siteId ? state.site.ianaTimeZoneName : undefined);
export const fetchTags = createAsyncThunk<IFetchedTagsPayload, IFetchTagsParams>(
  "multiSitesTagsChart/fetch",
  async ({ interval, paramsForGraphDisplayRequest, key }: IFetchTagsParams, { getState }) => {
    const siteTimeZone = requestSiteTimeZone(
      getState() as RootState,
      paramsForGraphDisplayRequest.siteId
    );
    const params = {
      ...paramsForGraphDisplayRequest,
      range: chartRangeView(paramsForGraphDisplayRequest.range),
    };
    const apiResponse = await getTagsHistoryAsync(params);
    validateResponse(apiResponse, params, interval);
    return {
      data: serializeChartValues(apiResponse),
      rangeTime: paramsForGraphDisplayRequest.range,
      key: key,
      siteId: paramsForGraphDisplayRequest.siteId,
      siteTimeZone,
    };
  }
);

export const fetchTagsLttb = createAsyncThunk<IFetchedTagsPayload, IFetchTagsLttbParams>(
  "multiSitesLttbTagsChart/fetch",
  async ({ paramsForGraphDisplayRequest, key }: IFetchTagsLttbParams, { getState }) => {
    const siteTimeZone = requestSiteTimeZone(
      getState() as RootState,
      paramsForGraphDisplayRequest.siteId
    );
    const apiResponse = await getTagsHistoryLttbAsync({
      ...paramsForGraphDisplayRequest,
      range: chartRangeView(paramsForGraphDisplayRequest.range),
    });
    return {
      data: serializeChartValues(apiResponse),
      rangeTime: paramsForGraphDisplayRequest.range,
      key: key,
      siteId: paramsForGraphDisplayRequest.siteId,
      siteTimeZone,
    };
  }
);

export const fetchTagsLatest = createAsyncThunk<
  IFetchedTagsPayload,
  IFetchHistoryTagsLatestParams,
  { state: RootState }
>(
  "multiSitesTagsChart/fetchLatest",
  async ({ interval, paramsForGraphDisplayRequest, key }: IFetchHistoryTagsLatestParams, thunkAPI) => {
    const state = thunkAPI.getState();
    const requestSiteId = paramsForGraphDisplayRequest.siteId;
    const timeZone =
      state.multiSitesMetadata?.sites[requestSiteId]?.ianaTimeZoneName ??
      (state.site.siteId === requestSiteId ? state.site.ianaTimeZoneName : undefined);
    const params = {
      ...paramsForGraphDisplayRequest,
      range: chartRangeView(paramsForGraphDisplayRequest.range),
    };
    const apiResponse = await getTagsHistoryLatestAsync(params);
    validateResponse(apiResponse, params, interval);
    const potential = apiResponse[Tags.Portal.DailyProduction.Potential.logicalName];
    if (potential && timeZone) {
      adjustPotentialByProduction(apiResponse, potential, new DateTimeFormatter(timeZone));
    }
    return {
      data: serializeChartValues(apiResponse),
      rangeTime: paramsForGraphDisplayRequest.range,
      key: key,
      siteId: paramsForGraphDisplayRequest.siteId,
      siteTimeZone: timeZone,
    };
  },
  {
    condition: (params: IFetchHistoryTagsLatestParams, { getState }) => {
      if (params.shouldFetchIfDataExists) {
        return true;
      }
      const { multiSitesChartsData } = getState();
      const siteId = params.paramsForGraphDisplayRequest.siteId;
      if (!multiSitesChartsData[siteId]) {
        return true;
      }
      const state = multiSitesChartsData[siteId][params.key];
      if (!state) {
        return true;
      }
      const status = state.status;
      const currentRangeTime = state.rangeTime;

      if (!(
        params.paramsForGraphDisplayRequest.range.start === currentRangeTime.start &&
        params.paramsForGraphDisplayRequest.range.end === currentRangeTime.end
      )) {
        return true;
      }
      return !(status === LoadingState.Complete || status === LoadingState.Pending);
    },
  }
);

export const multiSitesTagsChartReducer = multiSitesTagsChartSlice.reducer;
export const selectSiteChartsView = (state: RootState, siteId: string) =>
  chartSiteView(state.multiSitesChartsData?.[siteId]);
